import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import {
    isInitialPermissions,
    isInitialPermissionsGranted,
    isInitialPermissionsNotGranted,
    isRejectedOnlyVideo,
} from '@pexip/media';
import type {UserMediaStatus} from '@pexip/media';
import type {ExtendedSizeModifier, GroupProps} from '@pexip/components';
import {Button, Icon, IconTypes, Group} from '@pexip/components';

import {TestId} from '../../../test/testIds';
import type {
    BlockedBrowserPermissionsInfoType,
    DeviceStatusInfo,
    InputDeviceStatusInfo,
} from '../../types';
import type {TooltipPosition} from '../InputControl/PreflightInputControl.view';
import {PreflightInputControl} from '../InputControl/PreflightInputControl.view';
import {useInputsState} from '../../hooks/useInputsState';
import {BackgroundBlurInputControl} from '../InputControl/InputControl.view';
import {useInputSuspendedInfo} from '../../hooks/useInputSuspendedInfo';

export const PreflightInputControls: React.FC<
    GroupProps & {
        audioInputControl?: React.ReactNode;
        enhancerEnd?: React.ReactNode;
        inputStatusInfo: InputDeviceStatusInfo;
        isAudioInputMuted: boolean;
        isAudioInputSuspended: boolean;
        isAudioInputUnavailable: boolean;
        isBlurred?: boolean;
        isMobileDevice?: boolean;
        isRequestingAudio: boolean;
        isRequestingVideo: boolean;
        isVideoInputMuted: boolean;
        isVideoInputSuspended: boolean;
        isVideoInputUnavailable: boolean;
        learnHowToGrantAccessURL?: string;
        permissionInfoType: BlockedBrowserPermissionsInfoType;
        requestAudioPermissions: () => void;
        requestVideoPermissions: () => void;
        segmentationEnabled?: boolean;
        setShowHelpVideo: React.Dispatch<React.SetStateAction<boolean>>;
        silentTooltipInfo?: DeviceStatusInfo;
        streamStatus?: UserMediaStatus;
        toggleAudioInput?: () => void;
        toggleBlur?: () => void;
        toggleFacingMode?: () => void;
        toggleVideoInput?: () => void;
    }
> = ({
    audioInputControl,
    enhancerEnd,
    inputStatusInfo,
    isAudioInputMuted,
    isAudioInputSuspended,
    isAudioInputUnavailable,
    isBlurred,
    isMobileDevice = false,
    isRequestingAudio,
    isRequestingVideo,
    isVideoInputMuted,
    isVideoInputSuspended,
    isVideoInputUnavailable,
    learnHowToGrantAccessURL,
    permissionInfoType,
    requestAudioPermissions,
    requestVideoPermissions,
    segmentationEnabled,
    setShowHelpVideo,
    silentTooltipInfo,
    streamStatus,
    toggleAudioInput,
    toggleBlur,
    toggleFacingMode,
    toggleVideoInput,
    ...props
}) => {
    const {t} = useTranslation();

    const {
        audioProps,
        videoProps,
        audioWarning,
        videoWarning,
        isAudioIconMuted,
        isVideoIconMuted,
    } = useInputsState({
        isAudioInputMuted,
        isVideoInputMuted,
        inputStatusInfo,
        iconSize: 'medium',
    });

    const suspendedInfo = useInputSuspendedInfo(
        isAudioInputSuspended,
        isVideoInputSuspended,
    );
    const micDeniedInfo =
        inputStatusInfo.audio.error || inputStatusInfo.audio.warning
            ? inputStatusInfo.audio
            : undefined;
    const audioBlockingInfo = suspendedInfo.audio ?? micDeniedInfo;
    const audioStatusInfo = audioBlockingInfo ?? silentTooltipInfo;
    const videoBlockingInfo =
        suspendedInfo.video ??
        (inputStatusInfo.video.error || inputStatusInfo.video.warning
            ? inputStatusInfo.video
            : undefined);
    const videoStatusInfo = videoBlockingInfo;
    const canRetryAudio = Boolean(audioStatusInfo?.retriable);
    const canRetryVideo = Boolean(videoStatusInfo?.retriable);

    const [isAudioTooltipVisible, setIsAudioTooltipVisible] = useState(
        Boolean(audioWarning || (audioBlockingInfo && isAudioInputUnavailable)),
    );
    const [isVideoTooltipVisible, setIsVideoTooltipVisible] = useState(
        Boolean(videoWarning || (videoBlockingInfo && isVideoInputUnavailable)),
    );

    useEffect(() => {
        if (isAudioInputSuspended) {
            setIsAudioTooltipVisible(false);
            return;
        }
        setIsAudioTooltipVisible(
            Boolean(
                audioWarning ||
                silentTooltipInfo ||
                (audioBlockingInfo && isAudioInputUnavailable),
            ),
        );
    }, [
        audioBlockingInfo,
        audioWarning,
        isAudioInputSuspended,
        isAudioInputUnavailable,
        silentTooltipInfo,
    ]);

    useEffect(() => {
        setIsVideoTooltipVisible(
            Boolean(
                videoWarning || (videoBlockingInfo && isVideoInputUnavailable),
            ),
        );
    }, [isVideoInputUnavailable, videoBlockingInfo, videoWarning]);

    const [disableBgButton, setDisableBgButton] = useState(true);

    useEffect(() => {
        if (
            streamStatus &&
            !isInitialPermissionsNotGranted(streamStatus) &&
            !(
                isInitialPermissions(streamStatus) &&
                isInitialPermissionsGranted(streamStatus) &&
                isRejectedOnlyVideo(streamStatus)
            )
        ) {
            setDisableBgButton(
                isVideoInputMuted ||
                    Boolean(inputStatusInfo.video.error) ||
                    Boolean(videoWarning),
            );
        }
    }, [
        inputStatusInfo.video.error,
        isVideoInputMuted,
        streamStatus,
        videoWarning,
    ]);

    const coreInputProps: {
        size: ExtendedSizeModifier;
        tooltipPosition: TooltipPosition;
    } = {
        size: 'large',
        tooltipPosition: isMobileDevice ? 'topCenter' : 'topEnd',
    };
    let handleAudioBtnClick = toggleAudioInput;
    let handleVideoBtnClick = toggleVideoInput;
    if (audioBlockingInfo) {
        handleAudioBtnClick = () =>
            setIsAudioTooltipVisible(visible => !visible);
    }
    if (videoBlockingInfo) {
        handleVideoBtnClick = () =>
            setIsVideoTooltipVisible(visible => !visible);
    }

    return (
        <Group
            spacing={isMobileDevice ? 'small' : 'medium'}
            justifyContent="center"
            {...props}
        >
            {!isMobileDevice && !!segmentationEnabled && (
                <BackgroundBlurInputControl
                    variant="transparentAlternative"
                    size="large"
                    isBlurred={isBlurred}
                    isDisabled={isVideoInputUnavailable || disableBgButton}
                    onClick={toggleBlur}
                    iconSize="medium"
                    data-testid={
                        isBlurred
                            ? TestId.ButtonBackgroundEffectsOn
                            : TestId.ButtonBackgroundEffectsOff
                    }
                />
            )}
            {toggleFacingMode && (
                <Button
                    aria-label={t('media.flip-camera', 'Flip camera')}
                    data-testid={TestId.ButtonFlipCamera}
                    modifier="square"
                    onClick={toggleFacingMode}
                    size="large"
                    variant="transparentAlternative"
                    disabled={isVideoInputMuted}
                    isLoading={isRequestingVideo}
                >
                    <Icon source={IconTypes.IconFlipCamera} size="medium" />
                </Button>
            )}
            {enhancerEnd && enhancerEnd}
            {
                <PreflightInputControl
                    {...audioProps}
                    {...coreInputProps}
                    isDisabled={isAudioInputUnavailable && !canRetryAudio}
                    isLoading={isRequestingAudio}
                    isSuspended={isAudioInputSuspended}
                    isTooltipVisible={
                        (!isAudioInputUnavailable ||
                            Boolean(audioBlockingInfo)) &&
                        isAudioTooltipVisible
                    }
                    setIsTooltipVisible={setIsAudioTooltipVisible}
                    toolTipTestId={TestId.NotificationToolTipAudioProblem}
                    data-testid={
                        isAudioIconMuted
                            ? TestId.ButtonAudioMeterMuted
                            : TestId.ButtonAudioMeter
                    }
                    onClick={handleAudioBtnClick}
                    requestMediaPermissions={requestAudioPermissions}
                    testIdCloseButton={
                        TestId.NotificationToolTipAudioCloseButton
                    }
                    variant="transparentAlternative"
                    control={audioInputControl}
                    inputStatusInfo={audioStatusInfo}
                    setShowHelpVideo={setShowHelpVideo}
                    learnHowToGrantAccessURL={learnHowToGrantAccessURL}
                    permissionInfoType={permissionInfoType}
                />
            }
            {
                <PreflightInputControl
                    {...videoProps}
                    {...coreInputProps}
                    isDisabled={isVideoInputUnavailable && !canRetryVideo}
                    isLoading={isRequestingVideo}
                    isSuspended={isVideoInputSuspended}
                    isTooltipVisible={
                        (!isVideoInputUnavailable ||
                            Boolean(videoBlockingInfo)) &&
                        isVideoTooltipVisible
                    }
                    setIsTooltipVisible={setIsVideoTooltipVisible}
                    toolTipTestId={TestId.NotificationToolTipVideoProblem}
                    data-testid={
                        isVideoIconMuted
                            ? TestId.ButtonVideoinputMuted
                            : TestId.ButtonVideoInput
                    }
                    onClick={handleVideoBtnClick}
                    requestMediaPermissions={requestVideoPermissions}
                    testIdCloseButton={
                        TestId.NotificationToolTipVideoCloseButton
                    }
                    variant="transparentAlternative"
                    inputStatusInfo={videoStatusInfo}
                    setShowHelpVideo={setShowHelpVideo}
                    learnHowToGrantAccessURL={learnHowToGrantAccessURL}
                    permissionInfoType={permissionInfoType}
                />
            }
        </Group>
    );
};

export type PreflightInputControlsProps = React.ComponentProps<
    typeof PreflightInputControls
>;
